import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Merge Tailwind classes with clsx
 */
export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/* ============================================
   SCORE COLOR UTILITIES
   ============================================ */

/**
 * Get text color class based on score (0-100)
 * @param {number} score - Score value
 * @param {object} thresholds - Custom thresholds { high: 80, medium: 60 }
 * @returns {string} Tailwind text color class
 */
export function getScoreColor(score, thresholds = { high: 80, medium: 60 }) {
  if (score == null) return 'text-muted-foreground';
  if (score >= thresholds.high) return 'text-success';
  if (score >= thresholds.medium) return 'text-warning';
  return 'text-destructive';
}

/**
 * Get background color class based on score
 */
export function getScoreBgColor(score, thresholds = { high: 80, medium: 60 }) {
  if (score == null) return 'bg-muted/10';
  if (score >= thresholds.high) return 'bg-success/10';
  if (score >= thresholds.medium) return 'bg-warning/10';
  return 'bg-destructive/10';
}

/**
 * Get border color class based on score
 */
export function getScoreBorderColor(score, thresholds = { high: 80, medium: 60 }) {
  if (score == null) return 'border-border';
  if (score >= thresholds.high) return 'border-success/30';
  if (score >= thresholds.medium) return 'border-warning/30';
  return 'border-destructive/30';
}

/**
 * Get Domain Authority color (uses SEO thresholds)
 * DA >= 50: Good, DA >= 30: Medium, DA < 30: Low
 */
export function getDAColor(score) {
  if (score == null) return 'text-muted-foreground';
  if (score >= 50) return 'text-success';
  if (score >= 30) return 'text-warning';
  return 'text-muted-foreground';
}

/**
 * Get Page Authority color (same thresholds as DA)
 */
export function getPAColor(score) {
  return getDAColor(score);
}

/**
 * Get Spam Score color (inverted - higher is worse)
 * Moz uses 0-17 scale, DFS uses 0-100
 */
export function getSpamScoreColor(score, maxScale = 17) {
  if (score == null) return 'text-muted-foreground';

  // Normalize to percentage
  const normalized = maxScale === 100 ? score : (score / maxScale) * 100;

  if (normalized >= 60) return 'text-destructive';  // High spam
  if (normalized >= 30) return 'text-warning';       // Medium spam
  return 'text-success';                             // Low spam
}

/**
 * Get Quality Score color (0-1 scale)
 */
export function getQualityColor(score) {
  if (score == null) return 'text-muted-foreground';
  if (score >= 0.6) return 'text-success';
  if (score >= 0.35) return 'text-warning';
  return 'text-destructive';
}

/* ============================================
   STATUS STYLE UTILITIES
   ============================================ */

/**
 * Backlink status configuration with semantic tokens
 */
export const statusStyles = {
  // Critical statuses
  removed: {
    bg: 'bg-destructive',
    bgLight: 'bg-destructive/20',
    text: 'text-destructive-foreground',
    textLight: 'text-destructive',
    border: 'border-destructive/30',
    label: 'Lost',
    priority: 1,
  },
  '404': {
    bg: 'bg-destructive',
    bgLight: 'bg-destructive/20',
    text: 'text-destructive-foreground',
    textLight: 'text-destructive',
    border: 'border-destructive/30',
    label: '404',
    priority: 1,
  },
  '5xx': {
    bg: 'bg-destructive',
    bgLight: 'bg-destructive/20',
    text: 'text-destructive-foreground',
    textLight: 'text-destructive',
    border: 'border-destructive/30',
    label: 'Server Error',
    priority: 1,
  },

  // Warning statuses
  nofollow_changed: {
    bg: 'bg-warning/20',
    bgLight: 'bg-warning/10',
    text: 'text-warning',
    textLight: 'text-warning',
    border: 'border-warning/30',
    label: 'Nofollow',
    priority: 2,
  },
  redirected: {
    bg: 'bg-warning/20',
    bgLight: 'bg-warning/10',
    text: 'text-warning',
    textLight: 'text-warning',
    border: 'border-warning/30',
    label: 'Redirect',
    priority: 2,
  },
  timeout: {
    bg: 'bg-warning/20',
    bgLight: 'bg-warning/10',
    text: 'text-warning',
    textLight: 'text-warning',
    border: 'border-warning/30',
    label: 'Timeout',
    priority: 3,
  },

  // Info/Pending
  pending: {
    bg: 'bg-muted',
    bgLight: 'bg-muted/50',
    text: 'text-muted-foreground',
    textLight: 'text-muted-foreground',
    border: 'border-muted',
    label: 'Pending',
    priority: 4,
  },
  checking: {
    bg: 'bg-primary/20',
    bgLight: 'bg-primary/10',
    text: 'text-primary',
    textLight: 'text-primary',
    border: 'border-primary/30',
    label: 'Checking',
    priority: 4,
    animate: 'animate-pulse',
  },

  // Success
  live: {
    bg: 'bg-success/20',
    bgLight: 'bg-success/10',
    text: 'text-success',
    textLight: 'text-success',
    border: 'border-success/20',
    label: 'Live',
    priority: 5,
  },
};

/**
 * Get status styling by status name
 */
export function getStatusStyle(status) {
  return statusStyles[status] || statusStyles.pending;
}

/**
 * Get status priority for sorting
 */
export function getStatusPriority(status) {
  return statusStyles[status]?.priority || 10;
}

/* ============================================
   LINK TYPE UTILITIES
   ============================================ */

/**
 * Link type configuration with semantic tokens
 */
export const linkTypeStyles = {
  dofollow: {
    bg: 'bg-success/20',
    text: 'text-success',
    label: 'Follow',
  },
  nofollow: {
    bg: 'bg-warning/20',
    text: 'text-warning',
    label: 'Nofollow',
  },
  ugc: {
    bg: 'bg-accent/20',
    text: 'text-accent-foreground',
    label: 'UGC',
  },
  sponsored: {
    bg: 'bg-primary/20',
    text: 'text-primary',
    label: 'Sponsored',
  },
};

/**
 * Get link type styling
 */
export function getLinkTypeStyle(type) {
  return linkTypeStyles[type] || linkTypeStyles.dofollow;
}

/* ============================================
   CARD VARIANT UTILITIES
   ============================================ */

/**
 * Card variant styles
 */
export const cardVariants = {
  // Solid background variants (original)
  default: 'bg-card border-border',
  hero: 'bg-gradient-to-br from-primary/10 to-card border-primary/20',
  secondary: 'bg-secondary border-border/50',
  muted: 'bg-muted/50 border-border/50',
  interactive: 'bg-card border-border hover:border-primary/30 hover:bg-card/80 transition-all cursor-pointer',
  success: 'bg-success/10 border-success/20',
  warning: 'bg-warning/10 border-warning/20',
  destructive: 'bg-destructive/10 border-destructive/20',

  // Glowing border variants (modern, lighter aesthetic)
  glow: 'glow-card',
  'glow-filled': 'glow-card-filled',
  'glow-success': 'glow-card glow-success',
  'glow-warning': 'glow-card glow-warning',
  'glow-destructive': 'glow-card glow-destructive',
  'glow-info': 'glow-card glow-info',
  'glow-interactive': 'glow-card-filled cursor-pointer',
  'glow-hero': 'glow-card-filled glow-pulse',
};

/**
 * Get card variant classes
 */
export function getCardVariant(variant = 'default') {
  return cardVariants[variant] || cardVariants.default;
}

/* ============================================
   LINK HEALTH & PBN SCORING
   ============================================ */

/**
 * Sigmoid function for smooth normalization
 */
function sigmoid(value, midpoint) {
  return 1 / (1 + Math.exp(-0.05 * (value - midpoint)));
}

/**
 * Calculate link health score (0-1) using 9-factor formula
 * DFS Rank is the primary authority signal; Moz DA/PA are secondary validation.
 */
export function calculateLinkHealth({ da, pa, spamScore, traffic, referringDomains, referringIps, backlinksCount, dfsSpamScore, dfsRank, anchorDiversity, consecutiveFailures, lastStatusChangeDays, avgResponseMs }) {
  // Detect if DFS data is present
  const hasDfsData = dfsRank != null || (referringDomains || 0) > 0;

  // Common signals
  const authority = (da || 0) / 100;
  const pageStrength = (pa || 0) / 100;
  const mozSpamNorm = (spamScore || 0) / 17;
  const anchorDiv = anchorDiversity != null ? anchorDiversity : 0.5;
  const failureRate = Math.min((consecutiveFailures || 0) / 5, 1);
  const uptimeFactor = lastStatusChangeDays != null ? Math.min(lastStatusChangeDays / 30, 1) : 0.5;
  const stability = (1 - failureRate) * uptimeFactor;

  let score;

  if (hasDfsData) {
    // Full 9-factor formula (DFS + Moz)
    const dfsRankSignal = dfsRank != null
      ? 1 - sigmoid(dfsRank, 30000)
      : 0.3;
    const dfsSpamNorm = dfsSpamScore != null ? dfsSpamScore / 100 : 0;
    const spamRisk = Math.max(mozSpamNorm, dfsSpamNorm);
    const trafficSignal = sigmoid(traffic || 0, 500);
    const domainEcosystem = sigmoid(referringDomains || 0, 50);
    const ipDiversity = (referringIps || 0) > 0 && (referringDomains || 0) > 0
      ? Math.min(referringIps / referringDomains, 1)
      : 0.3;

    score = Math.max(0, Math.min(1,
      0.20 * dfsRankSignal
      + 0.05 * authority + 0.05 * pageStrength
      - 0.15 * spamRisk
      + 0.15 * trafficSignal + 0.15 * domainEcosystem
      + 0.05 * ipDiversity + 0.05 * anchorDiv
      + 0.15 * stability
    ));
  } else {
    // Moz-only formula — redistributed weights when DFS data is absent
    score = Math.max(0, Math.min(1,
      0.25 * authority
      + 0.15 * pageStrength
      - 0.20 * mozSpamNorm
      + 0.10 * anchorDiv
      + 0.20 * stability
    ));
  }

  // PBN penalty: reduce score for suspicious domains
  const { pbnScore, trustLevel } = calculatePBNScore({
    da: da || 0,
    traffic: traffic || 0,
    referringDomains: referringDomains || 0,
    referringIps: referringIps || 0,
    backlinksCount: backlinksCount || 0,
    spamScore: spamScore || 0,
    dfsSpamScore: dfsSpamScore != null ? dfsSpamScore : null,
  });
  const pbnPenalty = [1.0, 1.0, 0.90, 0.80, 0.65, 0.50, 0.40, 0.30][Math.min(pbnScore, 7)];
  score = score * pbnPenalty;

  let grade;
  if (score >= 0.60) grade = 'strong';
  else if (score >= 0.35) grade = 'moderate';
  else if (score >= 0.15) grade = 'weak';
  else grade = 'toxic';

  return { score: Math.round(score * 100) / 100, grade, partial: !hasDfsData, pbnScore, trustLevel };
}

/**
 * Get color class for health grade
 */
export function getHealthGradeColor(grade) {
  switch (grade) {
    case 'strong': return 'text-success';
    case 'moderate': return 'text-warning';
    case 'weak': return 'text-orange-400';
    case 'toxic': return 'text-destructive';
    default: return 'text-muted-foreground';
  }
}

/**
 * Get bg color class for health grade
 */
export function getHealthGradeBg(grade) {
  switch (grade) {
    case 'strong': return 'bg-success';
    case 'moderate': return 'bg-warning';
    case 'weak': return 'bg-orange-400';
    case 'toxic': return 'bg-destructive';
    default: return 'bg-muted';
  }
}

/* ============================================
   ANIMATION UTILITIES
   ============================================ */

/**
 * Check if user prefers reduced motion
 */
export function prefersReducedMotion() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Get animation class (respects reduced motion)
 */
export function getAnimationClass(animation, fallback = '') {
  if (prefersReducedMotion()) return fallback;
  return animation;
}

/* ============================================
   PBN SCORING & TRUST CLASSIFICATION
   ============================================ */

/**
 * Calculate PBN score (0-5) and trust classification.
 * Mirrors backend calculatePBNScore logic for client-side rendering.
 *
 * @param {{ da, traffic, referringDomains, referringIps, backlinksCount, spamScore, dfsSpamScore }} metrics
 * @returns {{ pbnScore: number, trustLevel: string, signals: string[] }}
 */
export function calculatePBNScore({
  da = 0,
  traffic = 0,
  referringDomains = 0,
  referringIps = 0,
  backlinksCount = 0,
  spamScore = 0,
  dfsSpamScore = null,
} = {}) {
  let pbnScore = 0;
  const signals = [];

  // DA/traffic mismatch
  if (da >= 20 && traffic != null && traffic < 100) {
    pbnScore++;
    signals.push('DA/traffic mismatch');
  }

  // Graduated inflated link profile (backlinks per referring domain ratio)
  const refs = referringDomains || 0;
  const bls = backlinksCount || 0;
  if (refs > 0) {
    const ratio = bls / refs;
    if (ratio > 500) {
      pbnScore += 3;
      signals.push(`Extreme link inflation (${Math.round(ratio)}:1)`);
    } else if (ratio > 50) {
      pbnScore += 2;
      signals.push(`High link inflation (${Math.round(ratio)}:1)`);
    } else if (ratio > 10) {
      pbnScore++;
      signals.push(`Elevated link ratio (${Math.round(ratio)}:1)`);
    }
  }

  // IP diversity — only when we have IP data
  if (refs > 0 && referringIps > 0 && referringIps / refs < 0.3) {
    pbnScore++;
    signals.push('Shared IP footprint');
  }

  // Zero traffic — only penalize when traffic data is confirmed 0, not missing
  if (traffic != null && traffic === 0) {
    pbnScore++;
    signals.push('Zero traffic');
  }

  // Spam score
  const mozNorm = (spamScore || 0) / 17;
  const dfsNorm = dfsSpamScore != null ? dfsSpamScore / 100 : 0;
  if (Math.max(mozNorm, dfsNorm) > 0.30) {
    pbnScore++;
    signals.push('High spam score');
  }

  pbnScore = Math.min(pbnScore, 7);

  let trustLevel;
  if (pbnScore >= 4) trustLevel = 'suspicious';
  else if (pbnScore >= 2) trustLevel = 'caution';
  else if (pbnScore <= 1 && da >= 20 && traffic > 500 && refs >= 10) trustLevel = 'trusted';
  else trustLevel = 'unknown';

  return { pbnScore, trustLevel, signals };
}

/**
 * Trust level display config
 */
export const trustLevelConfig = {
  trusted: {
    color: 'text-success',
    bg: 'bg-success/15',
    border: 'border-success/30',
    label: 'Trusted',
  },
  caution: {
    color: 'text-warning',
    bg: 'bg-warning/15',
    border: 'border-warning/30',
    label: 'Caution',
  },
  suspicious: {
    color: 'text-destructive',
    bg: 'bg-destructive/15',
    border: 'border-destructive/30',
    label: 'Suspicious',
  },
  unknown: {
    color: 'text-muted-foreground',
    bg: 'bg-muted/30',
    border: 'border-border',
    label: 'Unknown',
  },
};

/**
 * Quality grade display config
 */
export const qualityGradeConfig = {
  strong: { color: 'text-success', bg: 'bg-success/15', label: 'Strong' },
  moderate: { color: 'text-warning', bg: 'bg-warning/15', label: 'Moderate' },
  weak: { color: 'text-orange-400', bg: 'bg-orange-400/15', label: 'Weak' },
  toxic: { color: 'text-destructive', bg: 'bg-destructive/15', label: 'Toxic' },
};

/**
 * Estimate DFS API cost based on domain count and mode
 */
export function estimateDFSCost(domainCount, mode = 'auto') {
  if (domainCount === 0) return '0.00';
  if (mode === 'live') return (domainCount * 0.02).toFixed(2);
  if (mode === 'batch') return '0.06';
  // auto: <=2 use live, 3+ use batch
  return domainCount <= 2 ? (domainCount * 0.02).toFixed(2) : '0.06';
}

/**
 * Get quality grade from score
 */
export function getQualityGrade(score) {
  if (score == null) return null;
  if (score >= 0.60) return 'strong';
  if (score >= 0.35) return 'moderate';
  if (score >= 0.15) return 'weak';
  return 'toxic';
}
